import { Input, Output, EventEmitter, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators, FormArray, AbstractControl } from '@angular/forms';
import {
    CreateReservationDateComponent,
    IReservationDateForm,
    IReservationDateFormModel
} from '../create-reservation-date/create-reservation-date.component';
import { StringHelper } from 'src/app/shared/utils/string-helper';
import { ReservationService } from 'src/app/shared/services/rest/reservation.service';
import { Reservation } from 'src/app/shared/models/reservation';
import { Occupancy } from 'src/app/shared/models/occupancy';
import { DateHelper } from 'src/app/shared/utils/date-helper';
import { MessageService } from 'src/app/shared/services/message.service';
import { MessageType } from 'src/app/shared/models/message';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Constants } from 'src/app/shared/models/constants.model';
import { OccupancyService } from 'src/app/shared/services/rest/occupancy.service';
import { ModalDialogComponent } from 'src/app/shared/components/modal-dialog/modal-dialog.component';
import { StatusId } from 'src/app/shared/models/status-id';
import { LanguageService } from 'src/app/shared/services/language.service';
import { ConflictMessage, ValidationIssue } from 'src/app/shared/models/conflict-message.model';
import { TranslateService } from '@ngx-translate/core';
import { HttpHelper } from 'src/app/shared/utils/http-helper';
import { SearchForm } from '../../models/search-form';
import { ResourceType } from 'src/app/shared/models/resource-type';
import { TimeSlot, TimeSlotType } from 'src/app/shared/models/time-slot.model';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import { v4 as id4 } from 'uuid';

enum DeleteType {
    Reservation,
    Date
}

interface IConflictInfo {
    timeFrom: Date;
    timeTo: Date;
    occupancies: Array<any>;
    senderId: string;
}

export abstract class CreateReservationDialogComponent {

    constructor(
        reservationService: ReservationService,
        private occupancyService: OccupancyService,
        messages: MessageService,
        translate: TranslateService,
        private language: LanguageService,
        fb: FormBuilder,
        protected readonly cdr: ChangeDetectorRef,
    ) {
        this.reservationService = reservationService;
        this.messages = messages;
        this.translate = translate;
        this.fb = fb;
    }

    abstract get type(): ResourceType;

    get displaySeatsControl(): boolean {
        return this.type === ResourceType.Raum;
    }

    get numberControlText(): string {
        switch (this.type) {
            case ResourceType.Raum:
                return 'ROOMNUMBER';
            case ResourceType.Gerät:
                return 'DEVICENUMBER';
            default:
                throw Error(`Support for type '${this.type}' not implemented`);
        }
    }

    get dates(): FormArray {
        return this.form.get('dates') as FormArray;
    }

    set dates(array: FormArray) {
        this.form.formGroup.controls[nameof<IReservationForm>('dates')] = array;
    }

    get ignoreErroneousLessons(): boolean {
        return this.dates.length > 1 && !!this.form.getValue('ignoreErroneousLessons');
    }

    get allowSerialDateReservations() {
        return AppConfigService.settings.AllowFurtherEvents;
    }

    // state props

    get isNewReservation(): boolean {
        return !Boolean(this.params.idReservation);
    }

    get isReadonly(): boolean {
        return !this.isNewReservation
            && (this.reservation && this.reservation.StatusId === StatusId.rStorniert || !this.hasFutureSavedDates);
    }

    // display props

    get displaySaveNewDatesButton(): boolean {
        return !this.isNewReservation && this.hasNewDates;
    }

    get enableSaveNewDatesButton(): boolean {
        return !this.isReadonly && this.hasNewDates && this.form.valid;
    }

    get enableSaveButton(): boolean {
        return !this.isReadonly &&
            this.form.valid &&
            (!this.reservationConflictReport || this.reservationConflictReport.length === 0) &&
            (this.isNewReservation || this.hasReservationChanges);
    }

    get display1StSaveButton(): boolean {
        return !this.isReadonly && !(this.hasMultipleDates && this.isNewReservation);
    }

    get display2NdSaveButton(): boolean {
        return !this.isReadonly && !this.display1StSaveButton;
    }

    get displayStornoButton(): boolean {
        return !this.isReadonly && !this.isNewReservation;
    }

    get displayDates(): boolean {
        return !this.isNewReservation;
    }

    get enableFurtherDateButton(): boolean {
        return !this.isReadonly;
    }

    get displayNewDates(): boolean {
        return (this.isNewReservation && this.hasMultipleNewDates) || (!this.isNewReservation && this.hasNewDates);
    }

    // other props

    get hasReservationChanges(): boolean {
        return this.form.get('title').dirty || this.form.get('furtherDetails').dirty;
    }

    get hasMultipleDates(): boolean {
        return this.dates.length > 1;
    }

    get hasNewDates(): boolean {
        return this.dates.controls.filter(x => !x.value.id).length > 0;
    }

    get hasFutureSavedDates(): boolean {
        return !!this.reservation && this.reservation.hasFutureSavedDates();
    }

    get hasMultipleNewDates(): boolean {
        return this.dates.controls.filter(x => !x.value.id).length > 1;
    }

    get hasUnsavedDates(): boolean {
        return !!this.dates.controls && !!this.dates.controls.filter(c => c.dirty).length || this.hasNewDates;
    }

    get hasUnsavedDatesExceptNew(): boolean {
        return !!this.dates.controls && !!this.dates.controls
            .filter(c => c.dirty && !!c.get(nameof<IReservationDateForm>('id')).value).length;
    }

    get lastDate(): Date {
        const numDates = this.dates.controls.length;
        if (!numDates) {
            return new Date();
        }

        const date = new Date(this.dates.controls[numDates - 1].get(nameof<IReservationDateForm>('date')).value as Date);
        const fromTime = this.dates.controls[numDates - 1].get(nameof<IReservationDateForm>('dateTimeFrom')).value as number;
        DateHelper.setTimeFromMinutesOfDay(fromTime, date);
        return date;
    }

    get lastDateTimeTo(): number {
        const numDates = this.dates.controls.length;
        if (!numDates) {
            return 60;
        }

        return Number(this.dates.controls[numDates - 1].get(nameof<IReservationDateForm>('dateTimeTo')).value);
    }

    // checks if an invalid date is chosen
    get hasUnavailableDateChosen() {
        return this.unavailableDateRow !== null;
    }

    get singleConflict() {
        return Array.isArray(this.reservationConflictReport) && this.reservationConflictReport.length > 0
            ? this.reservationConflictReport.find(c => c !== undefined)
            : null;
    }

    public static readonly hourMultiplicator = 4; // 15m min. interval

    /**
     * Component input parameter object
     *
     * @type {ICreateReservationParams}
     * @memberof CreateReservationDialogBaseComponent
     */
    @Input() params: ICreateReservationParams;

    /**
     * Event emitted after save of reservation
     * returns the id of the saved reservation (idAnlass)
     *
     * @type {EventEmitter<string>} Location-header value
     * @memberof CreateReservationDialogBaseComponent
     */
    @Output() saved: EventEmitter<string> = new EventEmitter<string>();

    /**
     * Event emitted when modal should be closed
     *
     * @type {EventEmitter<string>}
     * @memberof CreateReservationDialogBaseComponent
     */
    @Output() canceld: EventEmitter<string> = new EventEmitter<string>();

    /**
     * Delete dialog
     *
     * @private
     * @type {ModalDialogComponent}
     * @memberof RoomReservationBookmarksComponent
     */
    @ViewChild('confirmDeleteDialog') private _confirmDeleteDialog: ModalDialogComponent;

    // services

    protected reservationService: ReservationService;
    protected messages: MessageService;
    protected translate: TranslateService;
    protected fb: FormBuilder;

    /**
     * Header text of confirmDeleteDialog
     *
     * @memberof CreateReservationDialogBaseComponent
     */
    confirmDeleteActionTitle = '';

    protected form: SearchForm<IReservationForm>;

    /**
     * Number of rows for textarea
     *
     * @memberof CreateReservationDialogBaseComponent
     */
    furtherDetailsRows = 3;

    /**
     * Input parameters for confirm-delete dialog
     *
     * @type {ConfirmDeleteInfo}
     * @memberof CreateReservationDialogBaseComponent
     */
    confirmDeleteInfo: ConfirmDeleteInfo;

    reservationInfo: { header: string, primary: string, secondary: string };

    displayFurtherDatesControl: boolean;

    /**
     * Shows loading cycle if set to true
     *
     * @type {boolean}
     * @memberof CreateReservationDialogBaseComponent
     */
    isLoading = true;

    /**
     * Loaded reservation if is not new
     *
     * @private
     * @type {Reservation}
     * @memberof CreateReservationDialogBaseComponent
     */
    protected reservation: Reservation;

    /**
     * stores the position of the first unavailable date choices
     */
    protected unavailableDateRow: number = null;

    resourceOccupancies: TimeSlot[];
    protected resourceLabelWithArticle: string;

    /**
     * will be populated when a booking conflict occures
     */
    public readonly reservationConflictReport: {
        timeFrom: string,
        timeTo: string,
        conflictingTimeSpans: { startTime: string, endTime: string }[],
        source: IConflictInfo
    }[] = [];

    static getDateId(dateControl: AbstractControl): number {
        const value = dateControl.get(nameof<IReservationDateForm>('id')).value;
        return value ? Number(value) : 0;
    }
    public abstract loadOccupanciesData(from: Date, to: Date);

    getDateByIndex(index: number): AbstractControl | null {
        return this.dates.controls[index] ? this.dates.controls[index] : null;
    }

    getDateIndexById(id: number): number | null {
        for (let i = 0; i < this.dates.length; i++) {
            if (this.dates.controls[i].value.id === id) {
                return i;
            }
        }
        return null;
    }

    getDateById(lessonId: number): AbstractControl | null {
        return this.dates.controls.find(x => x.value.id === lessonId);
    }

    /**
    * Handle click on "Go!" in CreateReservationInterval component
    *
    * @param {Date[]} dates
    * @memberof CreateReservationDialogBaseComponent
    */
    onFurtherDatesGenerated(dates: Date[]) {
        dates.forEach(d => {
            const control = CreateReservationDateComponent.createFormGroupModel(this.fb);
            control.get(nameof<IReservationDateForm>('date')).setValue(d);
            control.get(nameof<IReservationDateForm>('dateTimeFrom')).setValue(DateHelper.getMinutesOfDay(d));
            control.get(nameof<IReservationDateForm>('dateTimeTo')).setValue(this.lastDateTimeTo);

            this.dates.push(control);
        });

        this.displayFurtherDatesControl = false;
    }

    // button handlers

    /**
     * Handle click on add further dates
     *
     * @memberof CreateReservationDialogBaseComponent
     */
    onFurtherDateClick() {
        this.displayFurtherDatesControl = true;
    }

    /**
     * Handle click on save lesson
     *
     * @memberof CreateReservationDialogBaseComponent
     */
    onSaveReservationClick() {

        if (!this.ensureUnsavedDates(true)) {
            return;
        }

        this.isLoading = true;
        this.messages.clear();

        const res = this.getModelFromForm();

        if (this.isNewReservation) {
            // add
            const headers = [];
            if (this.ignoreErroneousLessons) {
                headers.push({ key: Constants.Header.Request.XIgnoreMessageIds, value: 'ANY' });
            }

            this.reservationService
                .add(res, headers)
                .toPromise()
                .then(
                    (reservation: HttpResponse<Reservation>) => {
                        // success
                        const location = this.getLocation(reservation);
                        this.isLoading = false;
                        this.saved.emit(location);
                    },
                    (error: HttpErrorResponse) => {
                        if (error.status === 409) {
                            this.handleReservationConflict(error);
                        } else {
                            console.error(error.error);
                            this.translate.get('ERROR_SAVE_RESERVATION').subscribe((text: string) => {
                                this.messages.add(text, MessageType.Error);
                            });
                        }
                        this.isLoading = false;
                    }
                );
        } else {
            // update
            this.reservationService.update(res)
                .toPromise()
                .then(
                    () => {
                        // success
                        this.reloadReservation(false, true);
                        this.translate.get('SUCCESS_SAVE_RESERVATION').subscribe((text: string) => {
                            this.messages.add(text, MessageType.Success);
                        });
                        this.isLoading = false;
                        this.saved.emit('update');
                    },
                    (error: HttpErrorResponse) => {
                        if (error.status === 409) {
                            this.handleReservationConflict(error);
                        } else {
                            console.error(error.error);
                            this.translate.get('ERROR_SAVE_RESERVATION').subscribe((text: string) => {
                                this.messages.add(text, MessageType.Error);
                            });
                        }
                        this.isLoading = false;
                    }
                );
        }
    }

    /**
     * Handle click on cancel reservation (stornieren)
     *
     * @memberof CreateReservationDialogBaseComponent
     */
    onCancelReservationClick() {
        if (this.isNewReservation) {
            throw new Error('New reservations can not be canceld');
        }

        this.confirmDeleteInfo = new ConfirmDeleteInfo(
            this.params.idReservation,
            DeleteType.Reservation,
            ''
        );

        this.translate.get('CONFIRM_CANCEL_RESERVATION', {
            designation: this.form.getValue('title')
        }).subscribe((text: string) => {
            this.confirmDeleteInfo.text = text;
        });

        this.translate.get('BTN_STORNO').subscribe((text: string) => {
            this.confirmDeleteActionTitle = text;
        });

        this._confirmDeleteDialog.openModal();
    }

    /**
     * Handle click on confirmation of deletion
     *
     * @memberof RoomReservationBookmarksComponent
     */
    onModalClickDelete() {
        switch (this.confirmDeleteInfo.type) {
            case DeleteType.Date:
                this.deleteDate(this.confirmDeleteInfo.id);
                break;
            case DeleteType.Reservation:
                this.deleteReservation();
                break;
            default:
                throw Error('Invalid value "' + this.confirmDeleteInfo.type + '" for ' + nameof<ConfirmDeleteInfo>('type'));
        }

        this.confirmDeleteInfo = null;
    }

    /**
     * Handle click on save on a single date item
     *
     * @param {number} index
     * @memberof CreateReservationDialogBaseComponent
     */
    onSaveDateClick(index: number) {
        if (this.isNewReservation) {
            throw new Error('Can not save a single date on new reservation');
        }

        const formGroup = this.getDateByIndex(index);
        if (!formGroup) {
            throw new Error(`could not find date with index "${index}"`);
        }

        if (formGroup.invalid) {
            throw new Error(`can not save invalid date form`);
        }

        this.isLoading = true;
        this.messages.clear();

        const model = this.getOccupanyFromForm(formGroup);

        if (model.LessonId) {
            // update
            this.occupancyService.update(model).toPromise().then(
                () => {
                    this.translate.get('SUCCESS_SAVE_OCCUPANCY').subscribe((text: string) => {
                        this.messages.add(text, MessageType.Success);
                    });

                    this.saved.emit('update');
                    this.isLoading = false;
                    this.reloadReservation(true, true);
                },
                (error: HttpErrorResponse) => {
                    if (error.status === 409) {
                        this.handleDateConflict(error, index);
                    } else {
                        this.messages.add('Error on saving reservation', MessageType.Error);
                        console.error(error);
                    }

                    this.isLoading = false;
                });
        } else {
            // saves of new items are handeld in onSaveDateBatchClick
            throw Error('Not implemented');
        }
    }

    onSaveDateBatchClick() {
        if (this.isNewReservation) {
            throw new Error('Can not batch save dates on new reservation');
        }

        if (!this.ensureUnsavedDates(false)) {
            return;
        }

        this.isLoading = true;
        this.messages.clear();

        const observers = new Array<Promise<void>>();
        for (let i = 0; i < this.dates.length; i++) {
            if (this.isNewDate(i)) {
                const promise = this.saveDate(i);
                observers.push(promise);
            }
        }

        Promise.all(observers).then(
            () => {
                // no failed saves
                this.saved.emit('update');
                this.isLoading = false;
                this.translate.get('SUCCESS_SAVE_OCCUPANCIES').subscribe((text: string) => {
                    this.messages.add(text, MessageType.Success);
                });
                this.reloadReservation(true, false);
            },
            () => {
                // at leas one failed
                this.isLoading = false;

                if (this.ignoreErroneousLessons) {
                    this.dates = this.fb.array([]);
                    this.reloadReservation(true, true);
                }
            }
        );
    }

    isNewDate(index: number): boolean {
        const id = CreateReservationDialogComponent.getDateId(this.dates.controls[index]);
        return id === 0;
    }

    /**
     * Save a date - the returned promise will alway resolve, to indicate completion of the operation
     *
     * @param {number} index
     * @returns {Promise<void>}
     * @memberof CreateReservationDialogBaseComponent
     */
    saveDate(index: number): Promise<void> {
        const dateControl = this.dates.controls[index];
        dateControl.get(nameof<IReservationDateForm>('message')).setValue('');

        const occupancy = this.getOccupanyFromForm(dateControl);
        const calleable = (resolve, reject) => {
            this.occupancyService.add(occupancy, [])
                .toPromise()
                .then(
                    (response: HttpResponse<void>) => {
                        const location = this.getLocation(response);
                        const x = location.split('/');
                        const id = Number(x[x.length - 1]);
                        dateControl.get(nameof<IReservationDateForm>('id')).setValue(id);
                        dateControl.markAsPristine();
                        resolve();
                    },
                    (error: HttpErrorResponse) => {
                        if (error.status === 409) {
                            if (!this.ignoreErroneousLessons) {
                                this.handleDateConflict(error, index);
                            } else {
                                const stringDate = this.language.formatDate(occupancy.DateTimeFrom);
                                this.translate.get('ERROR_SAVE_OCCUPANCY', { date: stringDate }).subscribe((text: string) => {
                                    this.messages.add(text, MessageType.Information);
                                });
                                console.error(error);
                            }
                        }
                        reject();
                    });
        };

        return new Promise(calleable);
    }

    /**
     * Handle delete of single date element
     * remove from model if unsafed, or delete from server
     *
     * @param {number} index
     * @returns
     * @memberof CreateReservationDialogBaseComponent
     */
    onDeleteDateClick(index: number) {
        const formGroup = this.getDateByIndex(index);
        if (!formGroup) {
            this.messages.add('no date-form with index ' + index, MessageType.Debug);
            return;
        }

        const id = CreateReservationDialogComponent.getDateId(this.dates.controls[index]);
        if (id === 0) {
            this.dates.removeAt(index);
        } else {
            this.confirmDeleteInfo = new ConfirmDeleteInfo(
                id,
                DeleteType.Date,
                ''
            );

            const ctrl = this.dates.controls[index];
            const date = ctrl.get(nameof<IReservationDateForm>('date')).value;
            const params = { timeFrom: new Date(date), timeTo: new Date(date) };

            DateHelper.setTimeFromMinutesOfDay(ctrl.get(nameof<IReservationDateForm>('dateTimeFrom')).value, params.timeFrom);
            DateHelper.setTimeFromMinutesOfDay(ctrl.get(nameof<IReservationDateForm>('dateTimeTo')).value, params.timeTo);

            this.translate.get('CONFIRM_CANCEL_OCCUPANCY', {
                date: this.language.formatDate(date),
                timeFrom: this.language.formatTime(params.timeFrom),
                timeTo: this.language.formatTime(params.timeTo)
            }).subscribe((text: string) => {
                this.confirmDeleteInfo.text = text;
            });

            this.translate.get('BTN_DELETE').subscribe((text: string) => {
                this.confirmDeleteActionTitle = text;
            });

            this._confirmDeleteDialog.openModal();
        }
    }

    /**
     * Restore date from its saved occupancy
     *
     * @param {number} index
     * @returns
     * @memberof CreateReservationDialogBaseComponent
     */
    onRestoreDateClick(index: number) {
        const formGroup = this.getDateByIndex(index);
        if (!formGroup) {
            this.messages.add('no date-form with index ' + index, MessageType.Debug);
            return;
        }

        const id = CreateReservationDialogComponent.getDateId(this.dates.controls[index]);
        if (id === 0) {
            this.messages.add('can not restore unsaved date (index ' + index + ')', MessageType.Debug);
            return;
        }

        const occupancy = this.reservation.Occupancies.filter(o => o.LessonId === id);
        if (occupancy.length === 0) {
            this.messages.add('could not find occupancy with id ' + id + ' in current reservation', MessageType.Debug);
            return;
        }

        this.dates.controls[index] = this.createDateFormGroup(occupancy[0]);
        this.dates.controls[index].markAsPristine();
    }

    /**
     * Register if a date chosen is unavailable
     * @param params
     */
    onRegisterUnavailableDateChoice(params) {
        this.unavailableDateRow = Array.isArray(params) ? params[0] : null;
    }

    // Data

    /**
     * Load data from server
     *
     * @private
     * @memberof CreateReservationDialogBaseComponent
     */
    protected abstract async loadData();

    protected abstract loadReservationsrecht();

    /**
     * Handle data loading errors
     *
     * @param {HttpErrorResponse} error
     * @param {string} object
     * @param {number} id
     * @memberof CreateReservationDialogBaseComponent
     */
    protected handleRequestError(error: HttpErrorResponse, object: string, id: number) {
        switch (error.status) {
            case 404:
                this.translate.get(`${object}_NOT_FOUND`, { id: id }).subscribe((text: string) => {
                    this.messages.add(text, MessageType.Error);
                });
                break;
            case 401:
                this.translate.get(`${object}_NO_PERMISSION`, { id: id }).subscribe((text: string) => {
                    const type = object === 'ROOM' ? MessageType.Waning : MessageType.Error;
                    this.messages.add(text, type);
                });
                break;
            default:
                console.error(error);
                this.messages.add(
                    'Error on loading reservation ' + this.params.idReservation + ' or room ' + this.params.idRessource,
                    MessageType.Error
                );
        }
    }

    /**
     * Initially create form
     *
     * @private
     * @memberof CreateReservationDialogBaseComponent
     */
    protected createForm() {
        const model: IReservationForm = {
            roomNumber: new FormControl({ value: '', disabled: true }, []),
            seats: new FormControl({ value: '', disabled: true }, []),
            date: new FormControl(new Date()),
            title: new FormControl('', [
                Validators.required,
                Validators.minLength(2),
                Validators.maxLength(255)
            ]),
            furtherDetails: new FormControl(''),
            dates: this.fb.array([]),
            ignoreErroneousLessons: new FormControl(false)
        };

        this.form = new SearchForm(this.fb.group(model));

        this.form.get('furtherDetails').valueChanges.forEach((value: string) => {
            if (value !== undefined) {
                this.furtherDetailsRows = StringHelper.hasAny(value) ? 8 : 3;
            }
        });

        // todo (nice to have): safe and preload information prevent dataloss on reload
    }

    /**
     * Apply loaded reservation to form
     *
     * @private
     * @param {boolean} preservePendingChanges
     * @param {boolean} preservePendingDates
     * @memberof CreateReservationDialogBaseComponent
     */
    protected applyReservationToForm(preservePendingChanges: boolean, preservePendingDates: boolean) {

        if (!preservePendingChanges) {
            // override reservation
            this.form.formGroup.patchValue({
                title: this.reservation.Designation,
                furtherDetails: this.reservation.FurtherInformation
            });

            this.form.formGroup.markAsPristine({ onlySelf: true });
        }

        // preserve unsafed items
        const arr = new Array<AbstractControl>();

        if (preservePendingDates) {
            for (let i = 0; i < this.dates.length; i++) {
                if (this.isNewDate(i)) {
                    arr.push(this.dates.controls[i]);
                }
            }
        }

        this.dates = this.fb.array(arr);

        this.reservation.Occupancies.forEach((o: Occupancy) => {
            this.dates.push(this.createDateFormGroup(o));
        });

        this.initReadonlyState();
    }

    /**
     * Create FormGroup for CreateReservationDateComponent from Occupancy
     *
     * @param {Occupancy} o
     * @returns {FormGroup}
     * @memberof CreateReservationDialogBaseComponent
     */
    createDateFormGroup(o: Occupancy): FormGroup {
        const group = CreateReservationDateComponent.createFormGroupModel(this.fb);
        group.setValue({
            id: o.LessonId,
            date: o.DateTimeFrom,
            dateTimeFrom: String(DateHelper.getMinutesOfDay(o.DateTimeFrom)),
            dateTimeTo: String(DateHelper.getMinutesOfDay(o.DateTimeTo)),
            message: ''
        });

        if (!o.IsEditable) {
            group.disable();
        }

        return group;
    }

    /**
     * Enable/disable controls as required
     *
     * @private
     * @memberof CreateReservationDialogBaseComponent
     */
    protected initReadonlyState() {
        if (this.isReadonly) {
            this.form.formGroup.disable();
        } else {
            this.form.get('roomNumber').disable();
            this.form.get('seats').disable();
        }
    }

    protected initPresetDate() {
        if (this.params !== undefined) {
            const data = {
                id: null,
                date: this.params.from.toISOString(),
                dateTimeFrom: String(DateHelper.getMinutesOfDay(this.params.from)),
                dateTimeTo: String(DateHelper.getMinutesOfDay(this.params.to)),
                message: ''
            } as IReservationDateFormModel;

            if (this.dates.controls[0] !== undefined) {
                this.dates.controls[0].setValue(data);
            } else {
                const form = CreateReservationDateComponent.createFormGroupModel(this.fb);
                form.setValue(data);
                this.dates.push(form);
            }
        }
    }

    /**
     * Reload the reservation from server and update the form model
     *
     * @private
     * @param {boolean} preservePendingChanges
     * @memberof CreateReservationDialogBaseComponent
     */
    private reloadReservation(preservePendingChanges: boolean, preservePendingDates: boolean) {
        this.isLoading = true;

        this.reservationService.get(String(this.params.idReservation)).toPromise().then(
            (rawData: HttpResponse<Reservation> | Reservation) => {
                const data = HttpHelper.getResponseData(rawData);
                this.reservation = Reservation.create(data);
                this.applyReservationToForm(preservePendingChanges, preservePendingDates);
                this.isLoading = false;
            },
            (error: HttpErrorResponse) => {
                this.handleRequestError(error, 'RESERVATION', this.params.idReservation);
                this.isLoading = false;
            }
        );
    }

    /**
     * Handle click on confirmation of deletion
     *
     * @memberof RoomReservationBookmarksComponent
     */
    deleteReservation() {
        this.isLoading = true;
        this.reservationService.delete(String(this.params.idReservation)).toPromise().then(
            () => {
                this.translate.get('SUCCESS_CANCEL_RESERVATION').subscribe((text: string) => {
                    this.messages.add(text, MessageType.Success);
                });
                this.canceld.emit('Reservation Canceld');
                this.isLoading = false;

                this.reloadReservation(false, false);
            },
            (error: HttpErrorResponse) => {
                this.translate.get('ERROR_CANCEL_RESERVATION').subscribe((text: string) => {
                    this.messages.add(text, MessageType.Error);
                });

                console.error(error);
                this.isLoading = false;
            });
    }

    deleteDate(id: number) {

        if (this.dates.length === 1) {
            this.translate.get('ERROR_CANCEL_OCCUPANCY_ONE_DATE_REQUIRED').subscribe((text: string) => {
                this.messages.add(text, MessageType.Error);
            });
            return;
        }

        this.isLoading = true;
        // delete
        this.occupancyService.delete(String(id)).toPromise().then(
            () => {

                this.translate.get('SUCCESS_CANCEL_OCCUPANCY').subscribe((text: string) => {
                    this.messages.add(text, MessageType.Success);
                });

                this.saved.emit('update');
                this.isLoading = false;

                // remove from list of unsaved items
                for (let i = 0; i < this.dates.length; i++) {
                    const itemId = CreateReservationDialogComponent.getDateId(this.dates.controls[i]);
                    if (itemId === id) {
                        this.dates.removeAt(i);
                        break;
                    }
                }
            },
            (error: HttpErrorResponse) => {
                if (error.status === 409) {
                    this.handleDateConflict(error, this.getDateIndexById(id));
                } else {
                    this.messages.add('Error on canceling reservation', MessageType.Error);
                    console.error(error);
                }
                this.isLoading = false;
            });
    }

    /**
     * Append ConflictMessage to date object
     *
     * @param {HttpErrorResponse} error
     * @param {number} index
     * @memberof CreateReservationDialogBaseComponent
     */
    handleDateConflict(error: HttpErrorResponse, index: number) {
        const conflicMessage = error.error as ConflictMessage;
        if (conflicMessage.HasErrors) {
            const msg = conflicMessage.Issues.map(x => x.Message).join('<br/>');
            this.dates.controls[index].get(nameof<IReservationDateForm>('message')).setValue(msg);
        } else {
            console.error(error);
        }
    }

    /**
     * Append ConflictMessage to reservation object
     *
     * @param {HttpErrorResponse} error
     * @memberof CreateReservationDialogBaseComponent
     */
    handleReservationConflict(error: HttpErrorResponse) {
        const conflicMessage = error.error as ConflictMessage;
        if (conflicMessage.HasErrors) {

            conflicMessage.Issues.forEach((i: ValidationIssue) => {
                if (i.ConflictingObjectType === 'Occupancy') {
                    const object = Occupancy.create(JSON.parse(i.ConflictingObject));
                    // try to find object
                    for (let index = 0; index < this.dates.length; index++) {
                        const control = this.dates.controls[index];

                        const start = new Date(control.get(nameof<IReservationDateForm>('date')).value);
                        DateHelper.setTimeFromMinutesOfDay(Number(control.get(nameof<IReservationDateForm>('dateTimeFrom')).value), start);

                        const end = new Date(control.get(nameof<IReservationDateForm>('date')).value);
                        DateHelper.setTimeFromMinutesOfDay(Number(control.get(nameof<IReservationDateForm>('dateTimeTo')).value), end);

                        if (start.getTime() === object.DateTimeFrom.getTime() && end.getTime() === object.DateTimeTo.getTime()) {
                            control.get(nameof<IReservationDateForm>('message')).setValue(i.Message);
                            return;
                        }
                    }
                    // not found, add as general message
                    this.messages.add(i.Message, MessageType.Error);
                } else {
                    this.messages.add(i.Message, MessageType.Error);
                }
            });
        } else {
            console.error(error);
        }
    }

    /**
     * Fetch location information from http-header
     *
     * @param {HttpResponse<any>} response
     * @returns {string}
     * @memberof CreateReservationDialogBaseComponent
     */
    getLocation(response: HttpResponse<any>): string {
        // success
        const location = response.headers.get('Location');
        if (location === undefined || StringHelper.isNullOrEmpty(location)) {
            this.messages.add('response did not conatin any location-header', MessageType.Debug);
            return '';
        }
        return location;
    }

    /**
     * Create Reservation model from form-data
     *
     * @private
     * @returns {Reservation}
     * @memberof CreateReservationDialogBaseComponent
     */
    private getModelFromForm(): Reservation {
        const res = new Reservation();
        res.Designation = this.form.getValue('title');
        res.FurtherInformation = this.form.getValue('furtherDetails');
        res.ResourceId = this.params.idRessource;

        if (!this.isNewReservation) {
            res.ReservationId = this.params.idReservation;
        }

        this.form.get('dates').value.forEach((date: any) => {
            const oc = new Occupancy();

            oc.DateTimeFrom = new Date(date.date);
            DateHelper.setTimeFromMinutesOfDay(date.dateTimeFrom, oc.DateTimeFrom);

            oc.DateTimeTo = new Date(date.date);
            DateHelper.setTimeFromMinutesOfDay(date.dateTimeTo, oc.DateTimeTo);

            res.Occupancies.push(oc);
        });
        return res;
    }

    /**
     * Create Occupancy model from form-data
     *
     * @private
     * @param {AbstractControl} dateForm
     * @returns {Occupancy}
     * @memberof CreateReservationDialogBaseComponent
     */
    private getOccupanyFromForm(dateForm: AbstractControl): Occupancy {
        const occ = new Occupancy();
        occ.ResourceId = this.params.idRessource;
        occ.ReservationId = this.params.idReservation;
        occ.LessonId = dateForm.get(nameof<IReservationDateForm>('id')).value;

        occ.DateTimeFrom = new Date(dateForm.get(nameof<IReservationDateForm>('date')).value);
        DateHelper.setTimeFromMinutesOfDay(dateForm.get(nameof<IReservationDateForm>('dateTimeFrom')).value, occ.DateTimeFrom);

        occ.DateTimeTo = new Date(dateForm.get(nameof<IReservationDateForm>('date')).value);
        DateHelper.setTimeFromMinutesOfDay(dateForm.get(nameof<IReservationDateForm>('dateTimeTo')).value, occ.DateTimeTo);

        return occ;
    }

    /**
     * Show message and return false if has pending occupancy changes
     *
     * @private
     * @param {boolean} includingNewOnes Consider new dates as unsaved changes
     * @returns {boolean}
     * @memberof CreateReservationDialogBaseComponent
     */
    private ensureUnsavedDates(includingNewOnes: boolean): boolean {
        if (!this.isNewReservation && (includingNewOnes && this.hasUnsavedDates || !includingNewOnes && this.hasUnsavedDatesExceptNew)) {
            this.translate.get('ERROR_UNSAVED_CHANGES_OCCUPANCIES').subscribe((text: string) => {
                this.messages.add(text, MessageType.Error);
            });

            return false;
        }
        return true;
    }

    protected resolveArticleLabel(labelKey: string) {
        this.translate.get(labelKey).subscribe(value => {
            this.resourceLabelWithArticle = value;
        });
    }

    protected promptConflictingTimesNotice(conflictInfo: IConflictInfo) {
        if (conflictInfo === null) { throw new Error('conflict info must not be falsy'); }

        const { timeFrom, timeTo, occupancies } = conflictInfo;
        const infoModel = {
            timeFrom: DateHelper.getCHTimeString(timeFrom),
            timeTo: DateHelper.getCHTimeString(timeTo),
            conflictingTimeSpans: occupancies.map(oc => ({
                startTime: DateHelper.getCHTimeString(oc.DateTimeFrom),
                endTime: DateHelper.getCHTimeString(oc.DateTimeTo)
            })),
            source: conflictInfo
        };

        this.updateConflictingNoticeTimes(infoModel);
    }

    protected dismissConflictingTimesNotice(senderId: string) {
        if (!senderId) { throw new Error('senderId parameter is required'); }

        // clear conflict reports
        if (!this.reservationConflictReport || this.reservationConflictReport.length === 0) {
            return;
        }

        let fi;
        while (-1 !== (fi = this.reservationConflictReport.findIndex(cr => cr.source.senderId === senderId))) {
            const x = this.reservationConflictReport.splice(fi, 1);
        }

        this.cdr.detectChanges();
    }

    updateConflictingNoticeTimes(conflictMessageInfo: {
        timeFrom: string;
        timeTo: string;
        conflictingTimeSpans: {
            startTime: string;
            endTime: string;
        }[];
        source: IConflictInfo;
    }) {

        const ci = this.reservationConflictReport.find(rc => rc.source.senderId === conflictMessageInfo.source.senderId);
        if (ci) {
            Object.assign(ci, conflictMessageInfo);
        } else {
            this.reservationConflictReport.push(conflictMessageInfo);
        }
        this.cdr.detectChanges();
    }

    generateResourceOccupancies(resourceOccupancies: TimeSlot[]) {

        const reservedSlots = resourceOccupancies
            .map(ts => TimeSlot.create(ts, ts.IdObject === -1 ? TimeSlotType.Closed : TimeSlotType.Occupied))
            .sortByProp(p => p.StartMinuteIndex);

        const vacOccs = reservedSlots.reduce((accu, tso, i, list) => {
            let { index: index } = accu;

            // append first range if first did not start from 0
            if (index < tso.StartMinuteIndex) {
                accu.slots.push({
                    IdObject: tso.IdObject,
                    StartMinuteIndex: index,
                    EndMinuteIndex: tso.StartMinuteIndex - 1,
                    SlotType: TimeSlotType.Vacant,
                    DateTimeFrom: DateHelper.setTimeFromMinutesOfDay(index, new Date()),
                    DateTimeTo: DateHelper.setTimeFromMinutesOfDay(tso.StartMinuteIndex - 1, new Date())
                });
            }

            // all other parts
            accu.slots.push({
                IdObject: tso.IdObject,
                StartMinuteIndex: tso.StartMinuteIndex,
                EndMinuteIndex: tso.EndMinuteIndex,
                SlotType: tso.SlotType,
                DateTimeFrom: tso.DateTimeFrom,
                DateTimeTo: tso.DateTimeTo
            });
            index = tso.EndMinuteIndex + 1;

            // append last range if previous did not reach range bound
            if (list.length - i === 1 && index < accu.indexLimit - 1) {
                accu.slots.push({
                    IdObject: tso.IdObject,
                    StartMinuteIndex: index,
                    EndMinuteIndex: accu.indexLimit - 1,
                    SlotType: TimeSlotType.Vacant,
                    DateTimeFrom: DateHelper.setTimeFromMinutesOfDay(tso.EndMinuteIndex, new Date()),
                    DateTimeTo: DateHelper.setTimeFromMinutesOfDay(accu.indexLimit - 1, new Date())
                });
                index = accu.indexLimit;
            }

            return Object.assign(accu, { index });

        }, { index: 0, slots: [], indexLimit: 60 * 24 });

        return vacOccs.slots;
    }

    /**
     * 
     * @returns unique univeral id generator
     */
    protected getUuid() {
        return id4();
    }
}

interface IReservationForm {
    roomNumber: FormControl;
    seats: FormControl;
    date: FormControl;
    title: FormControl;
    furtherDetails: FormControl;
    dates: FormArray;
    ignoreErroneousLessons: FormControl;
}

export interface ICreateReservationParams {
    idReservation: number;
    idRessource: number;
    from: Date;
    to: Date;
}

class ConfirmDeleteInfo {

    id: number;
    type: DeleteType;
    text: string;

    constructor(id: number, type: DeleteType, text: string) {
        this.id = id;
        this.text = text;
        this.type = type;
    }

}
